import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { T } from '../customer/customers.theme';
import {
  Dialog,
  DialogHeader,
  ErrorLine,
  PillField,
  PrimaryButton,
} from '../customer/CustomersUI';
import { setActiveUser } from '../../SignupAndLogin/loginSlice';
import { updateProfile } from './accountApi';

/**
 * Your own name and username.
 *
 * ── What was wrong with the old one ─────────────────────────────────────────
 *
 * It signed you out on success, and it did so BEFORE dispatching the updated
 * user — so the store was wiped and then written to, leaving a logged-out app
 * holding a user. Changing your display name is not a security event: the
 * token is issued against your id, which does not change, so there is nothing
 * to sign out for. It now updates in place and the new name appears in the app
 * bar behind the dialog.
 *
 * It also sent a `phone` it never showed a field for, and reported both
 * success and failure through `alert`, which blocks the one thread the app
 * has. Errors stay inside the dialog now, beside the fields that caused them.
 */

const COPY = {
  title: 'Edit Profile',
  name: 'Name',
  username: 'Username',
  save: 'Save',
  nameRequired: 'Enter a name',
  usernameRequired: 'Enter a username',
  failed: 'Could not save. Try again.',
  note: 'Your username is what you sign in with.',
};

export default function EditProfileDialog({ user, onClose, onSaved }) {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.login.token);

  const [name, setName] = React.useState(() => (user && user.name) || '');
  const [username, setUsername] = React.useState(() => (user && user.username) || '');

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async () => {
    if (busy) return;

    if (!name.trim()) return setError(COPY.nameRequired);
    if (!username.trim()) return setError(COPY.usernameRequired);

    setBusy(true);
    setError('');

    try {
      const saved = await updateProfile({
        id: user._id || user.id,
        payload: { name: name.trim(), username: username.trim() },
        token,
      });

      // The server populates the business on this response, so the whole user
      // can go back into the store — every screen reads the shop off it.
      if (saved) dispatch(setActiveUser(saved));

      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || COPY.failed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={430}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader title={COPY.title} onClose={onClose} locked={busy} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 18 }}>
          <PillField
            name="profile-name"
            value={name}
            onChange={(value) => {
              setName(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.name}
            disabled={busy}
            autoFocus
          />

          <PillField
            name="profile-username"
            value={username}
            onChange={(value) => {
              setUsername(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.username}
            disabled={busy}
          />
        </div>

        <div style={{ fontSize: 11.5, color: T.inkFaint, lineHeight: 1.6, paddingTop: 10 }}>
          {COPY.note}
        </div>

        <div style={{ paddingTop: 14 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          style={{ width: '100%', marginTop: error ? 12 : 18 }}
        >
          {COPY.save}
        </PrimaryButton>
      </div>
    </Dialog>
  );
}
